import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/* eslint-disable no-param-reassign */
/* eslint-disable compat/compat */
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { CAN_USE_CAMERA_EFFECTS } from '../utils';
import { MirrorEffect } from './cameraEffects/MirrorEffect';
import { BackgroundBlurEffect } from './cameraEffects/BackgroundBlurEffect';
class EffectsContainer {
  constructor() {
    this.effectsMap = {
      mirror: MirrorEffect,
      blur: BackgroundBlurEffect
    };
    this.effects = [];
  }
  async sync(newEffects) {
    for (const effect of this.effects) {
      if (!newEffects.includes(effect.name)) {
        effect.cleanup();
        this.effects = this.effects.filter(e => e.name !== effect.name);
      }
    }
    for (const effectName of newEffects) {
      const effect = this.effects.find(e => e.name === effectName);
      if (!effect) {
        const newEffect = new this.effectsMap[effectName]();
        try {
          await newEffect.init();
          this.effects.push(newEffect);
        } catch (e) {
          // noop
        }
      }
    }
    this.effects.sort((a, b) => b.priority - a.priority);
  }
  process(ctx, videoFrame) {
    let result = true;
    for (const effect of this.effects) {
      result = effect.process(ctx, videoFrame);
    }
    return result;
  }
  async init() {
    for (const effect of this.effects) {
      try {
        await effect.init();
      } catch (e) {
        // noop
      }
    }
  }
  cleanup() {
    for (const effect of this.effects) {
      effect.cleanup();
    }
  }
}
export default class CameraEffects {
  constructor() {
    this.effects = new EffectsContainer();
  }
  async setCameraEffects(newEffects) {
    this.effects.sync(newEffects);
  }
  async start(videoStream) {
    if (!CAN_USE_CAMERA_EFFECTS) {
      return videoStream;
    }
    const _videoStream$getVideo = videoStream.getVideoTracks(),
      _videoStream$getVideo2 = _slicedToArray(_videoStream$getVideo, 1),
      track = _videoStream$getVideo2[0];
    const ctx = new OffscreenCanvas(1, 1).getContext('2d', {
      willReadFrequently: true
    });
    const trackProcessor = new window['MediaStreamTrackProcessor']({
      track
    });
    const trackGenerator = new window['MediaStreamTrackGenerator']({
      kind: 'video'
    });
    const transformer = new window.TransformStream({
      transform: (videoFrame, controller) => {
        ctx.canvas.width = videoFrame.displayWidth;
        ctx.canvas.height = videoFrame.displayHeight;
        ctx.save();
        const shouldDrawFrame = this.effects.process(ctx, videoFrame);
        shouldDrawFrame && ctx.drawImage(videoFrame, 0, 0);
        ctx.restore();
        videoFrame.close();
        const newFrame = new window.VideoFrame(ctx.canvas, {
          timestamp: videoFrame.timestamp,
          alpha: 'discard'
        });
        controller.enqueue(newFrame);
      }
    });
    await this.effects.init();
    trackProcessor.readable.pipeThrough(transformer).pipeTo(trackGenerator.writable).catch(() => {});
    trackGenerator.onended = () => this.effects.cleanup();
    return new MediaStream([trackGenerator]);
  }
}